import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Lightbulb, TrendingDown, TrendingUp, Loader2, FileDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TablaProductos, TablaFacturas, TablaDevoluciones, money, num } from "@/components/ClientePerfilTablas";

function Kpi({ label, value, sub, tone, testid }) {
  const color = tone === "bad" ? "text-[#b53a25]" : tone === "warn" ? "text-[#8a5a00]" : "text-[#15241d]";
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5c7368]">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${color}`} style={{ fontFamily: "'Manrope', sans-serif" }}>{value}</div>
      {sub ? <div className="text-xs text-[#4a5c53] mt-0.5">{sub}</div> : null}
    </div>
  );
}

function Kpis({ k, enRango }) {
  const var12 = k.variacion_anual_pct;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="perfil-kpis">
      <Kpi testid="kpi-total" label="Total comprado" value={money(k.total_comprado)} sub={`${k.n_facturas} facturas · ticket ${money(k.ticket_promedio)}`} />
      <Kpi testid="kpi-ultima" label="Última compra" value={k.ultima_compra || "—"} tone={k.dias_desde_ultima_compra > 60 ? "warn" : null}
        sub={k.dias_desde_ultima_compra != null ? `hace ${k.dias_desde_ultima_compra} días${k.frecuencia_dias ? ` · compra cada ~${k.frecuencia_dias} d` : ""}` : null} />
      <Kpi testid="kpi-anual" label="Últimos 12 meses" value={money(k.ultimos_12m)} tone={var12 != null && var12 < 0 ? "warn" : null}
        sub={var12 != null ? <span className="inline-flex items-center gap-1">{var12 >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{var12}% vs año anterior</span> : "Sin año anterior para comparar"} />
      <Kpi testid="kpi-saldo" label="Saldo por cobrar" value={money(k.saldo_pendiente)} tone={k.saldo_vencido > 0 ? "bad" : null} sub={k.saldo_vencido > 0 ? `${money(k.saldo_vencido)} vencido` : "Sin vencidos"} />
      <Kpi testid="kpi-productos" label="Productos distintos" value={k.productos_distintos} sub={k.primera_compra ? `${enRango ? "Primera compra del periodo" : "Cliente desde"} ${k.primera_compra}` : null} />
      <Kpi testid="kpi-devuelto" label="Devoluciones" value={money(k.total_devuelto)} tone={k.devolucion_pct >= 5 ? "warn" : null} sub={`${num(k.devolucion_pct)}% de lo comprado`} />
    </div>
  );
}

function Recomendaciones({ recs, sugeridos }) {
  const tone = { alto: "border-[#f2c4bb] bg-[#fdf3f1]", medio: "border-[#e8c98a] bg-[#fff8e6]", bajo: "border-[#b2d0c2] bg-[#e9f2ee]" };
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-3" data-testid="perfil-recomendaciones">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c7368]"><Lightbulb size={14} />Cómo vender más a este cliente</div>
      {recs.length === 0 && <p className="text-sm text-[#4a5c53]">Cliente al día y comprando con regularidad.</p>}
      {recs.map((r, i) => <div key={i} className={`rounded border px-3 py-2 text-sm ${tone[r.nivel]}`} data-testid={`perfil-rec-${i}`}>{r.texto}</div>)}
      {sugeridos.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-[#15241d] mt-2 mb-1">Productos que compran clientes similares y él no:</div>
          <ul className="text-sm space-y-1">
            {sugeridos.map(s => (
              <li key={s.producto_comercial_id} className="flex justify-between gap-3" data-testid={`perfil-sugerido-${s.producto_comercial_id}`}>
                <span>{s.producto}</span><span className="text-xs text-[#5c7368] tabular-nums">{s.clientes_similares} clientes · {money(s.monto_similares)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function mesesCompletos(rows) {
  if (!rows.length) return [];
  const map = Object.fromEntries(rows.map(r => [r.mes, r.total]));
  const out = [];
  let [y, m] = rows[0].mes.split("-").map(Number);
  const [y2, m2] = rows[rows.length - 1].mes.split("-").map(Number);
  while (y < y2 || (y === y2 && m <= m2)) {
    const k = `${y}-${String(m).padStart(2, "0")}`;
    out.push({ mes: k, total: map[k] || 0 });
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

function Resumen({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-md border border-[#d1dbd6] bg-white p-4" data-testid="perfil-grafico-mensual">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-3">Compras por mes</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={mesesCompletos(data.ventas_mensuales).slice(-24)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5ece8" />
            <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => money(v)} />
            <Bar dataKey="total" fill="#205c45" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mt-4 mb-2">Por vendedor</div>
        <div className="flex flex-wrap gap-2">
          {data.vendedores.map((v, i) => (
            <span key={i} className="rounded border border-[#d1dbd6] px-2 py-1 text-xs" data-testid={`perfil-vendedor-${i}`}>{v.vendedor} · {v.facturas} fact. · <strong>{money(v.monto)}</strong></span>
          ))}
        </div>
      </div>
      <Recomendaciones recs={data.recomendaciones} sugeridos={data.sugeridos} />
    </div>
  );
}

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [aplicado, setAplicado] = useState(false);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async (r) => {
    setLoading(true);
    try {
      const params = {};
      if (r.desde) params.desde = r.desde;
      if (r.hasta) params.hasta = r.hasta;
      const { data } = await api.get(`/masters/clientes/${id}/perfil`, { params });
      setData(data);
      setError("");
      setAplicado(!!(r.desde || r.hasta));
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { cargar({ desde: "", hasta: "" }); }, [cargar]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const descargarPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {};
      if (aplicado && rango.desde) params.desde = rango.desde;
      if (aplicado && rango.hasta) params.hasta = rango.hasta;
      const r = await api.get(`/masters/clientes/${id}/perfil/pdf`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `perfil_${(data?.cliente?.razon_social || "cliente").substring(0, 30).replace(/\s+/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPdfLoading(false); }
  };

  const c = data?.cliente;
  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <button type="button" onClick={() => navigate("/clientes")} className="text-xs text-[#205c45] inline-flex items-center gap-1 mb-2" data-testid="perfil-volver"><ArrowLeft size={12} />Clientes</button>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Perfil de cliente</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="perfil-nombre">{c?.razon_social || "…"}</h1>
          {c && <p className="text-sm text-[#4a5c53] mt-1" data-testid="perfil-datos">{c.numero_identificacion} · {c.ciudad || "—"} · Vendedor: {c.vendedor_nombre || "sin asignar"} · Crédito {c.dias_credito || 0} días / cupo {money(c.cupo_credito)}</p>}
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-[11px]">Desde</Label><Input type="date" value={rango.desde} onChange={(e) => setRango(r => ({ ...r, desde: e.target.value }))} data-testid="perfil-desde" /></div>
          <div><Label className="text-[11px]">Hasta</Label><Input type="date" value={rango.hasta} onChange={(e) => setRango(r => ({ ...r, hasta: e.target.value }))} data-testid="perfil-hasta" /></div>
          <Button className="bg-[#205c45]" onClick={() => cargar(rango)} disabled={loading} data-testid="perfil-aplicar">{loading ? <Loader2 size={14} className="animate-spin" /> : "Aplicar"}</Button>
          {(rango.desde || rango.hasta) && <Button variant="outline" onClick={() => { setRango({ desde: "", hasta: "" }); cargar({ desde: "", hasta: "" }); }} data-testid="perfil-todo">Todo</Button>}
          <Button variant="outline" onClick={descargarPdf} disabled={!data || pdfLoading} data-testid="perfil-pdf-btn">
            {pdfLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />}PDF para visita
          </Button>
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-[#f2c4bb] bg-[#fdf3f1] p-4 text-sm text-[#7a2514]" data-testid="perfil-error">{error}</div>
      )}
      {data && (
        <>
          <Kpis k={data.kpis} enRango={aplicado} />
          <Tabs defaultValue="resumen">
            <TabsList className="bg-white border border-[#d1dbd6]">
              <TabsTrigger value="resumen" data-testid="perfil-tab-resumen">Resumen</TabsTrigger>
              <TabsTrigger value="productos" data-testid="perfil-tab-productos">Productos ({data.productos.length})</TabsTrigger>
              <TabsTrigger value="facturas" data-testid="perfil-tab-facturas">Facturas ({data.facturas.length})</TabsTrigger>
              <TabsTrigger value="devoluciones" data-testid="perfil-tab-devoluciones">Devoluciones ({data.devoluciones.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="resumen"><Resumen data={data} /></TabsContent>
            <TabsContent value="productos"><TablaProductos rows={data.productos} /></TabsContent>
            <TabsContent value="facturas"><TablaFacturas rows={data.facturas} /></TabsContent>
            <TabsContent value="devoluciones"><TablaDevoluciones rows={data.devoluciones} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
